import heic2any from 'heic2any';

const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];

export function isHeicFile(file: File): boolean {
  return HEIC_TYPES.includes(file.type) || /\.(heic|heif)$/i.test(file.name);
}

/**
 * Converts a HEIC/HEIF file to a JPEG blob for browser compatibility.
 * Falls back to the original file if conversion fails.
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  if (!isHeicFile(file)) return file;

  try {
    const jpegBlobs = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    });

    const jpegBlob = Array.isArray(jpegBlobs) ? jpegBlobs[0] : jpegBlobs;
    const baseName = file.name.replace(/\.[a-zA-Z0-9]+$/, '');

    return new File([jpegBlob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (error) {
    console.warn('HEIC conversion failed, falling back to original file:', error);
    return file;
  }
}

export interface PixelCropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CreateCroppedImageFileParams {
  imageSrc: string;
  cropAreaPixels: PixelCropArea;
  fileName: string;
  outputType?: string;
  quality?: number;
}

interface ImageDimensions {
  width: number;
  height: number;
}

function loadImage(imageSrc: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image for cropping'));
    image.src = imageSrc;
  });
}

export async function getImageDimensions(imageSrc: string): Promise<ImageDimensions> {
  const image = await loadImage(imageSrc);
  return {
    width: image.naturalWidth || image.width,
    height: image.naturalHeight || image.height,
  };
}

export function isWholeImageCrop(
  cropAreaPixels: PixelCropArea,
  imageDimensions: ImageDimensions,
  tolerance = 2,
): boolean {
  const cropX = Math.max(0, Math.round(cropAreaPixels.x));
  const cropY = Math.max(0, Math.round(cropAreaPixels.y));
  const cropWidth = Math.max(1, Math.round(cropAreaPixels.width));
  const cropHeight = Math.max(1, Math.round(cropAreaPixels.height));
  const cropRight = cropX + cropWidth;
  const cropBottom = cropY + cropHeight;

  return (
    cropX <= tolerance &&
    cropY <= tolerance &&
    Math.abs(cropRight - imageDimensions.width) <= tolerance &&
    Math.abs(cropBottom - imageDimensions.height) <= tolerance
  );
}

function toBlob(canvas: HTMLCanvasElement, outputType: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to generate cropped image blob'));
          return;
        }
        resolve(blob);
      },
      outputType,
      quality,
    );
  });
}

function withImageExtension(fileName: string, outputType: string): string {
  const ext = outputType === 'image/png' ? 'png' : outputType === 'image/webp' ? 'webp' : 'jpg';
  const baseName = fileName.replace(/\.[a-zA-Z0-9]+$/, '');
  return `${baseName}_cover_crop.${ext}`;
}

export async function createCroppedImageFile({
  imageSrc,
  cropAreaPixels,
  fileName,
  outputType = 'image/jpeg',
  quality = 0.92,
}: CreateCroppedImageFileParams): Promise<File> {
  const image = await loadImage(imageSrc);
  const cropX = Math.max(0, Math.round(cropAreaPixels.x));
  const cropY = Math.max(0, Math.round(cropAreaPixels.y));
  const cropWidth = Math.max(1, Math.round(cropAreaPixels.width));
  const cropHeight = Math.max(1, Math.round(cropAreaPixels.height));

  const canvas = document.createElement('canvas');
  canvas.width = cropWidth;
  canvas.height = cropHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Unable to create canvas context for image cropping');
  }

  context.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

  const blob = await toBlob(canvas, outputType, quality);
  const outputName = withImageExtension(fileName, outputType);

  return new File([blob], outputName, {
    type: outputType,
    lastModified: Date.now(),
  });
}
